import { convexHull, type OperatorRigSink, type OperatorRigState, type Point3, type RigPart } from "./operatorRig";
import type { Vector } from "./vector";
import { RIG_IDENTITY, rigProjection, type RigTransform } from "./operatorKinematics";

interface PartWorkspace {
    parameters: number[];
    template: Point3[];
    vertices: Point3[];
    projected: Vector[];
}
const circle = Array.from({ length: 8 }, (_, i) => [Math.cos(i * Math.PI / 4), Math.sin(i * Math.PI / 4)]);

/** Collision-only workspace owned by one actor. Reuses primitive topology without
 * lighting, normals or ink. Published world hitboxes copy these mutable points. */
export class OperatorCollisionRig implements OperatorRigSink {
    private readonly cache = new Map<string, PartWorkspace>();
    private readonly parts: RigPart[] = [];
    private cy = 1;
    private sy = 0;
    private projection = rigProjection(1, 0);

    begin(state: OperatorRigState): RigPart[] {
        const yaw = Math.atan2(Math.sin(state.rotation) / 0.54, Math.cos(state.rotation));
        this.cy = Math.cos(yaw); this.sy = Math.sin(yaw);
        this.setTransform(RIG_IDENTITY);
        this.parts.length = 0;
        return this.parts;
    }

    setTransform(transform: RigTransform): void { this.projection = rigProjection(this.cy, this.sy, transform); }

    private entry(name: string, count: number): PartWorkspace {
        let item = this.cache.get(name);
        if (!item || item.vertices.length !== count) {
            item = { parameters: [], template: [],
                vertices: Array.from({ length: count }, () => ({ x: 0, y: 0, z: 0 })),
                projected: Array.from({ length: count }, () => ({ x: 0, y: 0 })) };
            this.cache.set(name, item);
        }
        return item;
    }

    private project(item: PartWorkspace, vertices: Point3[]): Vector[] {
        const m = this.projection;
        for (let i = 0; i < vertices.length; i++) {
            const v = vertices[i], q = item.projected[i];
            q.x = m[0] * v.x + m[1] * v.y + m[2] * v.z + m[3];
            q.y = m[4] * v.x + m[5] * v.y + m[6] * v.z + m[7];
        }
        return item.projected;
    }

    mesh(name: string, vertices: Point3[], faces: number[][], _color: number, _ink: boolean, _normals: Point3[] | undefined, convex: boolean): void {
        const points = this.project(this.entry(name, vertices.length), vertices);
        if (convex) { this.parts.push({ name, hull: convexHull(points), surfaces: [], convex }); return; }
        // Open meshes keep separate visible faces: their convex envelope would fill gaps.
        const surfaces = [];
        for (const face of faces) {
            const a = points[face[0]], b = points[face[1]], c = points[face[2]];
            if ((b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x) <= 0.000001) continue;
            surfaces.push({ points: face.map(i => points[i]), depth: 0, color: 0 });
        }
        this.parts.push({ name, hull: [], surfaces, convex });
    }

    private template(name: string, parameters: number[], make: () => Point3[]): PartWorkspace {
        const previous = this.cache.get(name);
        if (previous && previous.parameters.length === parameters.length && parameters.every((n, i) => n === previous.parameters[i])) return previous;
        const vertices = make(), item = this.entry(name, vertices.length);
        item.template = vertices; item.parameters = parameters;
        return item;
    }

    private translated(name: string, item: PartWorkspace, center: Point3): void {
        for (let i = 0; i < item.template.length; i++) {
            const a = item.template[i], b = item.vertices[i];
            b.x = center.x + a.x; b.y = center.y + a.y; b.z = center.z + a.z;
        }
        this.parts.push({ name, hull: convexHull(this.project(item, item.vertices)), surfaces: [], convex: true });
    }

    ellipsoid(name: string, center: Point3, size: Point3, _color: number, segments: number, rings: number, phiEnd: number): void {
        const item = this.template(name, [size.x, size.y, size.z, segments, rings, phiEnd], () => {
            const vertices: Point3[] = [];
            for (let r = 0; r <= rings; r++) for (let i = 0; i < segments; i++) {
                const phi = phiEnd * r / rings, theta = 2 * Math.PI * i / segments;
                vertices.push({ x: size.x * Math.sin(phi) * Math.cos(theta), y: size.y * Math.sin(phi) * Math.sin(theta), z: size.z * Math.cos(phi) });
            }
            return vertices;
        });
        this.translated(name, item, center);
    }

    box(name: string, center: Point3, size: Point3, _color: number, bevel: number): void {
        const item = this.template(name, [size.x, size.y, size.z, bevel], () => {
            const oct = [[-1, -1 + bevel], [-1 + bevel, -1], [1 - bevel, -1], [1, -1 + bevel],
                [1, 1 - bevel], [1 - bevel, 1], [-1 + bevel, 1], [-1, 1 - bevel]];
            return [-1, 1].flatMap(z => oct.map(([x, y]) => ({ x: x * size.x, y: y * size.y, z: z * size.z })));
        });
        this.translated(name, item, center);
    }

    limb(name: string, a: Point3, b: Point3, radius: number, _color: number): void {
        const item = this.entry(name, 16);
        let dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
        const length = Math.hypot(dx, dy, dz) || 1;
        dx /= length; dy /= length; dz /= length;
        let ux = -dy, uy = dx, ul = Math.hypot(ux, uy);
        if (ul < 0.001) { ux = 1; uy = 0; ul = 1; }
        ux /= ul; uy /= ul;
        const vx = -dz * uy, vy = dz * ux, vz = dx * uy - dy * ux;
        for (let end = 0; end < 2; end++) for (let i = 0; i < 8; i++) {
            const center = end ? b : a, v = item.vertices[end * 8 + i], [cos, sin] = circle[i];
            v.x = center.x + radius * (ux * cos + vx * sin);
            v.y = center.y + radius * (uy * cos + vy * sin);
            v.z = center.z + radius * vz * sin;
        }
        this.parts.push({ name, hull: convexHull(this.project(item, item.vertices)), surfaces: [], convex: true });
    }
}
